import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import whitePaperPreview from "@/assets/whitepaper-preview.png";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ensureHubSpotScript, createHubSpotForm, HUBSPOT_LIGHT_CSS } from "@/lib/hubspot";

const HubSpotWhitePaperForm = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";
      ensureHubSpotScript(() => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = `<div id="whitepaper-hs-form"></div>`;
        createHubSpotForm({
          region: "eu1",
          portalId: "25538277",
          formId: "6d3a94b3-612e-4181-97ac-71c211999990",
          target: "#whitepaper-hs-form",
          css: HUBSPOT_LIGHT_CSS,
        });
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}>
      <div ref={containerRef} className="min-h-[200px] [&_.hs-form-frame]:w-full" />
    </motion.div>
  );
};

const ease = [0.16, 1, 0.3, 1] as const;

const WhitePaperDownload = () => {
  const { i18n } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>Download White Paper – CetaFuel | Cetasol</title>
        <meta name="description" content="Download our white paper: How to measure fuel consumption on vessels without physical flow meters." />
        <link rel="canonical" href="https://cetasol.com/whitepaper/cetafuel-download" />
        <meta property="og:title" content="Download White Paper – CetaFuel | Cetasol" />
        <meta property="og:description" content="Download our white paper: How to measure fuel consumption on vessels without physical flow meters." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://cetasol.com/whitepaper/cetafuel-download" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download White Paper – CetaFuel | Cetasol" />
        <meta name="twitter:description" content="Download our white paper: How to measure fuel consumption on vessels without physical flow meters." />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
      </Helmet>
      <Header />
      <main className="pt-24 pb-0">
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-7xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6 leading-[1.1]">
                  Download White Paper
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Fill out the form to download our white paper: <strong>How to measure fuel consumption on vessels – without physical flow meters.</strong>
                </p>
                <img src={whitePaperPreview} alt="CetaFuel White Paper preview" className="mt-8 rounded-lg shadow-lg w-full max-w-[530px]" style={{ imageRendering: 'auto' }} />
              </motion.div>
              <HubSpotWhitePaperForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WhitePaperDownload;
